import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/feature/Header';
import Footer from '../components/feature/Footer';
import { GA_MEASUREMENT_ID, openConsentPreferences } from '@/utils/consent';

const linkClass = 'underline font-semibold text-primary-800 dark:text-primary hover:text-primary-900';

interface Row {
  nome: string;
  tipo: string;
  finalidade: string;
  duracao: string;
}

const ESSENCIAIS: Row[] = [
  {
    nome: 'stib_palestra_consent_v1',
    tipo: 'Armazenamento local (próprio)',
    finalidade: 'Guarda a sua escolha de privacidade, para não perguntarmos de novo a cada página.',
    duracao: '12 meses',
  },
  {
    nome: 'theme',
    tipo: 'Armazenamento local (próprio)',
    finalidade: 'Lembra se você prefere o modo claro, escuro ou automático.',
    duracao: 'Até você limpar o navegador',
  },
  {
    nome: 'exitIntentShown',
    tipo: 'Armazenamento de sessão (próprio)',
    finalidade: 'Evita repetir o aviso de oferta na mesma visita.',
    duracao: 'Até fechar a aba',
  },
];

function Table({ rows }: { rows: Row[] }) {
  return (
    <div className="mb-6 overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
      <table className="min-w-full text-left font-body text-sm text-gray-700 dark:text-gray-300">
        <thead className="bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white">
          <tr>
            <th className="px-4 py-3 font-semibold">Nome</th>
            <th className="px-4 py-3 font-semibold">Tipo</th>
            <th className="px-4 py-3 font-semibold">Para que serve</th>
            <th className="px-4 py-3 font-semibold">Duração</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
          {rows.map((r) => (
            <tr key={r.nome}>
              <td className="px-4 py-3 font-mono text-xs">{r.nome}</td>
              <td className="px-4 py-3">{r.tipo}</td>
              <td className="px-4 py-3">{r.finalidade}</td>
              <td className="px-4 py-3">{r.duracao}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const H2 = ({ children }: { children: React.ReactNode }) => (
  <h2 className="mt-12 mb-4 font-heading text-2xl font-bold text-gray-900 dark:text-white">{children}</h2>
);
const P = ({ children }: { children: React.ReactNode }) => (
  <p className="mb-4 font-body text-base leading-relaxed text-gray-700 dark:text-gray-300">{children}</p>
);

export default function PoliticaCookiesPage() {
  useEffect(() => {
    document.title = 'Política de Cookies | Palestra Silenciosa SP — STIB';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        'Veja quais cookies e tecnologias semelhantes o site da Palestra Silenciosa SP (STIB) usa, para que servem e como aceitar, recusar ou mudar sua escolha a qualquer momento.'
      );
    }
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', 'https://stibpalestrasilenciosasp.com.br/politica-cookies');
    const robots = document.querySelector('meta[name="robots"]');
    const previousRobots = robots ? robots.getAttribute('content') : null;
    if (robots) robots.setAttribute('content', 'noindex, follow');
    window.scrollTo(0, 0);
    return () => {
      if (robots && previousRobots !== null) robots.setAttribute('content', previousRobots);
    };
  }, []);

  return (
    <>
      <Header />
      <main className="min-h-screen bg-white dark:bg-gray-900">
        <section className="bg-gradient-to-br from-primary/5 to-secondary/10 dark:from-gray-800 dark:to-gray-900 pt-36 pb-12">
          <div className="max-w-3xl mx-auto px-6">
            <nav aria-label="Breadcrumb" className="mb-6 flex items-center gap-2 font-body text-sm text-gray-600 dark:text-gray-300">
              <Link to="/" className="hover:underline">Início</Link>
              <span aria-hidden="true">/</span>
              <span aria-current="page">Política de Cookies</span>
            </nav>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">
              Política de Cookies
            </h1>
            <p className="mt-4 font-body text-base text-gray-600 dark:text-gray-300">
              Versão 1.0 — vigente desde 10 de outubro de 2026. Aqui está, em linguagem simples, o que o site guarda
              no seu navegador e por quê.
            </p>
            <div className="mt-6">
              <button
                type="button"
                onClick={openConsentPreferences}
                className="inline-flex items-center justify-center rounded-full bg-primary px-6 py-3 font-body text-sm font-semibold text-gray-900 hover:bg-primary-400 cursor-pointer"
              >
                Abrir minhas preferências de privacidade
              </button>
            </div>
          </div>
        </section>

        <article className="max-w-4xl mx-auto px-6 pb-20">
          <H2>O que são cookies</H2>
          <P>
            Cookies são pequenos arquivos que um site grava no seu navegador. Neste texto, usamos o termo também para
            tecnologias parecidas, como o armazenamento local do navegador (localStorage e sessionStorage).
          </P>

          <H2>Essenciais (sempre ativos)</H2>
          <P>Necessários para o site funcionar e para respeitar as suas escolhas. Não dependem de consentimento.</P>
          <Table rows={ESSENCIAIS} />

          <H2>Com a sua permissão</H2>
          <P>Só são carregados depois que você aceita, e você pode retirar a permissão quando quiser.</P>
          <div className="mb-6 space-y-4">
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
              <h3 className="font-body text-base font-semibold text-gray-900 dark:text-white">
                Conteúdo de terceiros: mapas do Google
              </h3>
              <p className="mt-1 font-body text-sm leading-relaxed text-gray-700 dark:text-gray-300">
                Usado nas páginas de contato e de locais. Ao carregar o mapa, o Google recebe seu IP e pode gravar
                cookies próprios, sujeitos à política de privacidade do Google. Sem a sua permissão, mostramos apenas
                um botão para carregar o mapa ou abri-lo no Google Maps.
              </p>
            </div>
            {GA_MEASUREMENT_ID !== '' && (
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                <h3 className="font-body text-base font-semibold text-gray-900 dark:text-white">
                  Análise de audiência: Google Analytics
                </h3>
                <p className="mt-1 font-body text-sm leading-relaxed text-gray-700 dark:text-gray-300">
                  Cookies _ga e _ga_* (até 2 anos) que medem visitas e páginas mais vistas, com IP anonimizado. Não são
                  usados para publicidade.
                </p>
              </div>
            )}
          </div>

          <H2>Recursos de terceiros carregados com o site</H2>
          <P>
            Para exibir o site, o seu navegador busca fontes de texto no Google Fonts e ícones na rede jsDelivr. Eles
            não gravam cookies de rastreamento para nós, mas, como em qualquer pedido na internet, recebem o seu
            endereço IP para entregar o arquivo. Mais detalhes na{' '}
            <Link to="/politica-privacidade" className={linkClass}>Política de Privacidade</Link>.
          </P>

          <H2>Como mudar sua escolha</H2>
          <P>
            Use o botão{' '}
            <button type="button" onClick={openConsentPreferences} className={linkClass + ' cursor-pointer'}>
              Preferências de cookies
            </button>{' '}
            aqui, no rodapé de qualquer página ou no aviso do mapa. Você também pode apagar os dados do site nas
            configurações do navegador; nesse caso, vamos perguntar sua preferência de novo.
          </P>

          <H2>Dúvidas</H2>
          <P>
            Fale com o nosso encarregado de dados em{' '}
            <a className={linkClass} href="mailto:traducao@stibtraducaosimultanea.com.br">traducao@stibtraducaosimultanea.com.br</a>.
          </P>
        </article>
      </main>
      <Footer />
    </>
  );
}
